import React, { useState } from 'react'
import { Popconfirm, message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import getOrgAndEngineers from '../../api/getOrgAndEngineers'
import { URLBACK } from '../../assets/urls'
import { Organization, deleteUser } from '../../reducers/organizationReducer'
import { setIsOnboarded } from '../../reducers/playersReducer'
import { User } from '../../reducers/userReducer'
import messageInteraction from '../Analytics/utils/messageInteraction'
import { useAxiosWithHeader } from '../../utils'
import InviteAdminModal from './inviteUserModal'
import { isSlackAuth } from '../TeamChannels/helpers'

const role = (type, userRole) => {
  if (type === 'billing') return 'Billing'
  if (type === 'restrictedAccess') return 'Restricted Access'
  if (userRole.name === 'Engineer') return 'Engineer'
  return 'Admin'
}

async function handleRemove({ axios, dispatch, user, organization }) {
  try {
    const res = await axios.delete(
      `${URLBACK}organizations/manageAxoloAdmin/${organization.id}?userToDeleteId=${user.id}`,
    )
    if (res.status !== 200) {
      message.error('Error deleting user. Please try again or contact support')
      console.log('Error deleting user: ', user)
    } else {
      dispatch(deleteUser(user))
      message.success('User deleted.')
    }
  } catch (e) {
    message.error('Error deleting user. Please try again or contact support')
    console.log('Error deleting user: ', e)
  }
}

async function handleInviteAdmin({
  axios,
  email,
  organization,
  slackAuth,
  dispatch,
  user,
}) {
  try {
    const res = await axios.get(
      `${URLBACK}organizations/grantAdminRole/${organization.id}?email=${email}`,
    )
    if (res.status !== 200) {
      messageInteraction({
        content: 'Error granting an admin. Please try again or contact support',
        type: 'error',
      })
      console.log('Error granting an admin: ')
    } else {
      messageInteraction({ content: `${email} was added as an admin.`, type: 'success' })
      // todo here only update admin from backend not the whole organization
      getOrgAndEngineers({
        organization,
        slackAuth,
        dispatch,
        user,
        axios,
        setIsOnboarded,
        requestRefresh: true,
      })
    }
  } catch (e) {
    messageInteraction({
      content: 'Error granting an admin. Please try again or contact support',
      type: 'error',
    })
    console.log('Error granting an admin: ', e)
  }
}

function UserLine({ person, personIdx }) {
  const userRole = role(person.type, person.role)
  const axios = useAxiosWithHeader()
  const organization = useSelector(Organization.selectors.getOrganization)
  const user = useSelector(User.selectors.selectUser)

  const slackAuth = isSlackAuth({ user, organization })

  const dispatch = useDispatch()
  return (
    <tr key={person.email} className={personIdx % 2 === 0 ? undefined : 'bg-gray-50'}>
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
        {person.username?.split('_')?.[0] || person.username}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {person.email}
      </td>
      <td className="flex items-center justify-between whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {userRole}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {userRole === 'Engineer' && (
          <Popconfirm
            title={`Are you sure you want to grant admin role to ${person.username}`}
            onConfirm={() =>
              handleInviteAdmin({
                axios,
                email: person.email,
                organization,
                slackAuth,
                dispatch,
                user,
              })
            }
            okText="Yes"
            cancelText="No"
          >
            <button
              type="button"
              className="ml-2 inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-2 py-2 text-sm font-medium text-white shadow-sm hover:bg-hoverPrimary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              Grant admin role
            </button>
          </Popconfirm>
        )}
        {user.id !== person.id && (
          <Popconfirm
            title={`Are you sure you want to delete user ${person.email}`}
            onConfirm={() =>
              handleRemove({
                axios,
                dispatch,
                user: person,
                organization,
              })
            }
            okText="Yes"
            cancelText="No"
          >
            <button
              type="button"
              className="ml-2 inline-flex items-center justify-center rounded-md border border-transparent bg-red-500 px-2 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              Remove user
            </button>
          </Popconfirm>
        )}
      </td>
    </tr>
  )
}

export default function Example() {
  const organization = useSelector(Organization.selectors.getOrganization)
  const [openAccessModal, setOpenAccessModal] = useState(false)
  const [openInviteAdminModal, setOpenInviteAdminModal] = useState(false)

  const { users = [] } = organization
  const admins = users.filter((user) => user.role.name === 'Authenticated')
  return (
    <div className="">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <p className="">
            See who has admin access on Axolo or grant admin priviledge to new users.
            <br />
            Access to settings and billing sections is exclusive to admin and billing
            roles.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setOpenInviteAdminModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-hoverPrimary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new administrator
          </button>
          <InviteAdminModal
            open={openInviteAdminModal}
            setOpen={setOpenInviteAdminModal}
          />
        </div>
      </div>
      <div className="mt-3 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {admins.map((person, personIdx) => (
                    <UserLine
                      // eslint-disable-next-line react/no-array-index-key
                      key={personIdx}
                      person={person}
                      personIdx={personIdx}
                      open={openAccessModal}
                      setOpen={setOpenAccessModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
